import * as React from 'react'
import * as Sentry from '@sentry/react'
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth'
import { useNavigate } from 'react-router'
import { $path } from 'safe-routes'

import { useRouteUtils } from '@modules/route/routeUtils'
import { toast } from '@modules/shadcn/ui/Toast/use-toast'
import { safeDataLayer } from '@modules/utils/gtm'

import { authApp, firebaseLogout, googleLogin } from '@admin/auth/firebase'
import { AppStateContainer } from '@admin/context/AppStateContainer'
import type { GetAdminMeResponse } from '@admin/gen_proto/api_proto/v1/admin_pb'
import { AdminService } from '@admin/gen_proto/api_proto/v1/admin_service_pb'
import { setAuthTokenRequired, useClient } from '@admin/request/client'

// 「ログアウトボタンによるサインアウト」かどうかを、認証リスナー（useAuthListener）と
// ログアウト操作（useAuthActions）の間で共有するフラグ。
// セッション切れトーストを出すかどうかの分岐にだけ使う。
// Company側（front/src/company/src/auth/auth.ts）は1つのhook内のrefで持つが、
// Adminはリスナーと操作を別hookに分けたのでモジュール変数に置く。
// 認証リスナーはアプリ全体で1本（root.tsxのみ）なので競合しない。
let isExplicitLogout = false

/**
 * Firebaseの認証状態を監視し、ログイン中の管理者情報をContextへ入れる。
 *
 * **root.tsxからのみ呼ぶこと**。呼ぶたびにリスナーが1本増え、GetAdminMeもその本数だけ飛ぶ。
 * login / logoutだけが欲しい場所はuseAuthActionsを使う。
 */
export const useAuthListener = () => {
  const { updateAdmin } = AppStateContainer.useContainer()
  const [loading, setLoading] = React.useState(true)
  const client = useClient(AdminService)

  // 「直前まで認証済みだったか」の覚書。onAuthStateChangedのコールバックは
  // renderサイクルの外で発火するのでstateではなくrefで持つ。
  // セッション切れトーストを「一度ログインできていた場合だけ」出すために使う。
  const wasAuthenticatedRef = React.useRef(false)

  const setUserInfo = (data: GetAdminMeResponse) => {
    updateAdmin(data)

    // sentry: 個人を特定できる氏名・メールアドレスは送らない（id から DB で引ける）
    Sentry.setUser({ id: data.id.toString() })

    // gtm
    // NOTE: data.id は proto int64 由来の bigint。GTM の変数解決層が
    // bigint を URL クエリにエンコードできずパラメータを無音でドロップするため、
    // dataLayer に push する前に必ず String() で文字列化する。
    // 参考: doc/coding_rules/all.md「BigInt / Protobuf シリアライズ規約」
    // NOTE: AdminにはGA4のlogin event送信が無いため、Company側にある
    // isNewLogin（login eventの重複防止）の判定は持たない。
    safeDataLayer({
      event: 'setDimension',
      admin_Id: String(data.id),
    })
  }

  React.useEffect(() => {
    // ここから先はアプリ本体の動作。トークンが取れないリクエストは送らせない
    setAuthTokenRequired()

    const handleSignedOut = () => {
      // 明示的なログアウト以外の経路でセッションが切れた場合のみ通知する
      // （別タブでのログアウト・refresh token失効・アカウント無効化など）
      if (wasAuthenticatedRef.current && !isExplicitLogout) {
        toast({
          title: 'セッションが切れました',
          description: '再度ログインしてください',
          variant: 'destructive',
        })
      }
      wasAuthenticatedRef.current = false
      isExplicitLogout = false
      // Contextに管理者情報を残さない。残すとログイン済みとして画面が動き続け、
      // 以後のRPCが失敗し続けてもログイン画面に戻らない
      updateAdmin(null)
      Sentry.setUser(null)
    }

    const cancelAuthListener = onAuthStateChanged(authApp, async user => {
      if (user) {
        try {
          const data = await client.getAdminMe({})
          // 取得できてから「認証済みだった」と記録する。失敗時はfalseのままにして、
          // 次回の発火で改めて評価できるようにする
          wasAuthenticatedRef.current = true
          setUserInfo(data)
        } catch (error) {
          console.error(error)
          // サインアウトするとonAuthStateChangedがuser=nullで再発火し、
          // handleSignedOutがContextをクリアしてログイン画面へ戻す
          firebaseLogout()
        }
      } else {
        handleSignedOut()
      }
      setLoading(false)
    })

    // refresh token失敗時はonAuthStateChangedより先にuser=nullで発火するケースがあるため購読
    const cancelTokenListener = onIdTokenChanged(authApp, user => {
      if (!user) {
        handleSignedOut()
      }
    })

    return () => {
      cancelAuthListener()
      cancelTokenListener()
    }
  }, [])

  return { loading }
}

/**
 * ログイン・ログアウトの操作だけを返す。認証リスナーは張らない。
 * 画面から呼んでよいのはこちら。
 */
export const useAuthActions = () => {
  const { updateAdmin } = AppStateContainer.useContainer()
  const navigate = useNavigate()
  const { getFullPath } = useRouteUtils()

  const login = async () => {
    await googleLogin()
  }

  const logout = async () => {
    isExplicitLogout = true
    await firebaseLogout()
    // リスナー側のhandleSignedOutでも同じことをするが、発火を待たずに
    // ログイン画面へ切り替えるためここでもクリアする
    updateAdmin(null)
    Sentry.setUser(null)

    const currentPath = getFullPath()
    const path = currentPath ? $path('/login', { next: currentPath }) : $path('/login')
    navigate(path)
  }

  return { login, logout }
}

export const useUnAuthMiddleware = () => {
  const { admin } = AppStateContainer.useContainer()
  const navigate = useNavigate()
  const { getSearchQuery } = useRouteUtils()
  const search = getSearchQuery()

  const next = search.get('next')

  React.useEffect(() => {
    if (admin) {
      const nextPath = next ?? '/'
      navigate(nextPath)
    }
  }, [admin])
}
